import React, { useState, useEffect } from 'react';
import { useOKR } from '../contexts/OKRContext';
import LoadingSpinner from './LoadingSpinner';

const OKRProgress = ({ objective, onSuccess, onCancel }) => {
  const { updateKeyResult, loading, error, clearError } = useOKR();
  
  const [keyResultUpdates, setKeyResultUpdates] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    // Initialize key result updates with current values
    const initialUpdates = {};
    objective.key_results?.forEach(kr => {
      initialUpdates[kr.id] = {
        current_value: kr.current_value || 0,
        is_completed: kr.is_completed || false,
        title: kr.title,
        description: kr.description,
        target_value: kr.target_value,
        unit: kr.unit
      };
    });
    setKeyResultUpdates(initialUpdates);
  }, [objective]);

  useEffect(() => {
    if (error) {
      setFormErrors({ general: error });
    }
  }, [error]);

  const handleKeyResultUpdate = (keyResultId, field, value) => {
    setKeyResultUpdates(prev => ({
      ...prev,
      [keyResultId]: {
        ...prev[keyResultId],
        [field]: value
      }
    }));

    // Clear field-specific errors
    if (formErrors[`${keyResultId}_${field}`]) {
      setFormErrors(prev => ({
        ...prev,
        [`${keyResultId}_${field}`]: null
      }));
    }
  };

  const calculateKeyResultScore = (keyResult, update) => {
    const { type, target_value } = keyResult;
    const { current_value, is_completed } = update;

    switch (type) {
      case 'boolean':
        return is_completed ? 1.0 : 0.0;
      case 'percentage':
        return Math.min(1.0, (current_value || 0) / 100.0);
      case 'numeric':
      case 'milestone':
        if (target_value && target_value > 0) {
          return Math.min(1.0, (current_value || 0) / target_value);
        }
        return 0.0;
      default:
        return 0.0;
    }
  };

  const calculateObjectiveScore = () => {
    if (!objective.key_results || objective.key_results.length === 0) {
      return 0.0;
    }

    const totalScore = objective.key_results.reduce((sum, kr) => {
      const update = keyResultUpdates[kr.id];
      const score = calculateKeyResultScore(kr, update);
      return sum + score;
    }, 0);

    return totalScore / objective.key_results.length;
  };

  const formatProgressValue = (keyResult, update) => {
    const { type, target_value, unit } = keyResult;
    const { current_value, is_completed } = update;

    switch (type) {
      case 'boolean':
        return is_completed ? 'Completed' : 'Not Completed';
      case 'percentage':
        return `${current_value || 0}%`;
      case 'numeric':
      case 'milestone':
        const target = target_value || 0;
        const current = current_value || 0;
        return `${current}${unit ? ` ${unit}` : ''} / ${target}${unit ? ` ${unit}` : ''}`;
      default:
        return `${current_value || 0}${unit ? ` ${unit}` : ''}`;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 0.7) return 'text-green-600';
    if (score >= 0.4) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressColor = (score) => {
    if (score >= 0.7) return 'bg-green-500';
    if (score >= 0.4) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const validateUpdates = () => {
    const errors = {};

    objective.key_results?.forEach(kr => {
      const update = keyResultUpdates[kr.id];
      
      if (kr.type !== 'boolean' && update.current_value < 0) {
        errors[`${kr.id}_current_value`] = 'Value cannot be negative';
      }

      if (kr.type === 'percentage' && update.current_value > 100) {
        errors[`${kr.id}_current_value`] = 'Percentage cannot exceed 100%';
      }

      if (kr.type === 'milestone' && kr.target_value && update.current_value > kr.target_value) {
        errors[`${kr.id}_current_value`] = 'Current value cannot exceed target';
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateUpdates()) {
      return;
    }

    setSubmitLoading(true);
    clearError();

    try {
      // Update each key result
      const updatePromises = objective.key_results.map(kr => {
        const update = keyResultUpdates[kr.id];
        return updateKeyResult(kr.id, {
          current_value: kr.type === 'boolean' ? kr.current_value : update.current_value,
          is_completed: update.is_completed
        });
      });

      await Promise.all(updatePromises);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error updating key results:', error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const objectiveScore = calculateObjectiveScore();

  return (
    <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Update OKR Progress</h2>
        <h3 className="text-lg text-gray-700 mb-1">{objective.title}</h3>
        {objective.description && (
          <p className="text-gray-600">{objective.description}</p>
        )}
      </div>

      {/* Current Objective Score */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Current Objective Score</span>
          <span className={`text-lg font-bold ${getScoreColor(objectiveScore)}`}>
            {(objectiveScore * 100).toFixed(0)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all duration-300 ${getProgressColor(objectiveScore)}`}
            style={{ width: `${Math.min(objectiveScore * 100, 100)}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          This score updates automatically based on key result progress
        </p>
      </div>

      {formErrors.general && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{formErrors.general}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Key Results Progress */}
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">Key Results Progress</h4>
          
          {objective.key_results?.map(keyResult => {
            const update = keyResultUpdates[keyResult.id] || {};
            const score = calculateKeyResultScore(keyResult, update);
            
            return (
              <div key={keyResult.id} className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="mb-3">
                  <h5 className="font-medium text-gray-900 mb-1">{keyResult.title}</h5>
                  {keyResult.description && (
                    <p className="text-gray-600 text-sm mb-2">{keyResult.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <span>Type: {keyResult.type}</span>
                    <span className={`font-medium ${getScoreColor(score)}`}>
                      Score: {(score * 100).toFixed(0)}%
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(score)}`}
                      style={{ width: `${Math.min(score * 100, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Update Inputs */}
                {keyResult.type === 'boolean' ? (
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={update.is_completed || false}
                        onChange={(e) => handleKeyResultUpdate(keyResult.id, 'is_completed', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Mark as completed</span>
                    </label>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Value
                      </label>
                      <input
                        type="number"
                        value={update.current_value || 0}
                        onChange={(e) => handleKeyResultUpdate(keyResult.id, 'current_value', parseFloat(e.target.value) || 0)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          formErrors[`${keyResult.id}_current_value`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        step="0.01"
                        min="0"
                        max={keyResult.type === 'percentage' ? 100 : keyResult.target_value}
                      />
                      {formErrors[`${keyResult.id}_current_value`] && (
                        <p className="text-red-500 text-sm mt-1">{formErrors[`${keyResult.id}_current_value`]}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Progress
                      </label>
                      <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                        {formatProgressValue(keyResult, update)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Completion Checkbox for non-boolean types */}
                {keyResult.type !== 'boolean' && (
                  <div className="mt-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={update.is_completed || false}
                        onChange={(e) => handleKeyResultUpdate(keyResult.id, 'is_completed', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Mark as completed (regardless of target)</span>
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition duration-200"
            disabled={submitLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 disabled:opacity-50"
          >
            {submitLoading ? (
              <div className="flex items-center">
                <LoadingSpinner size="sm" />
                <span className="ml-2">Updating...</span>
              </div>
            ) : (
              'Update Progress'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OKRProgress;